import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { MtmaContact } from 'src/app/model/mtma-contact';
import { FormControl } from '@angular/forms';
import {
	CONTACTS_DEFAULT_SORT_COLUMN, CONTACTS_DEFAULT_SORT_COLUMN_DIRECTION,
	CONTACTS_PAGE_DEFAULT_SIZE, CONTACTS_PAGE_SIZE_OPTIONS, CONTACTS_TABLE_HEADER
} from 'src/app/conf/mtma-contacts';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { Command } from 'src/app/model/command';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectMtmaActions, selectMtmaActiveTask, selectMtmaCommandAutomation, selectMtmaContactModify, selectMtmaContacts, selectMtmaContactsMaxData, selectMtmaContactsPagination } from 'src/app/store/device/device.selector';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { Lang } from 'src/app/model/lang';
import { MtmaTask } from 'src/app/model/mtma-task';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MtmaContactDialogComponent } from '../mtma-manage-contact-dialog/mtma-manage-contact-dialog.component';
import { StandardMtmaLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { MtmaIndexTab } from 'src/app/model/device';

@Component({
	selector: 'app-mtma-manage-contact',
	templateUrl: './mtma-manage-contact.component.html',
	providers: [ConfirmationService, DialogService]
})
export class MtmaManageContactComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	mtmaContacts$: Observable<MtmaContact[] | undefined>;
	mtmaContactsSub: Subscription | undefined;
	mtmaContactsPagination$: Observable<ItemsPagination | undefined>;
	mtmaContactsPaginationSub: Subscription | undefined;
	mtmaContactsMaxData$: Observable<MtmaMaxData | undefined>;
	mtmaContactsMaxDataSub: Subscription | undefined;
	mtmaCommandAutomation$: Observable<Command[] | undefined>;
	mtmaCommandAutomationSub: Subscription | undefined;
	mtmaActions$: Observable<MtmaAction[] | undefined>;
	mtmaActionsSub: Subscription | undefined;
	mtmaContactModify$: Observable<boolean | undefined>;
	mtmaContactModifySub: Subscription | undefined;
	currentLang$: Observable<Lang> = new Observable();
	currentLangSub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	filterSub: Subscription;
	searchFilter: FormControl = new FormControl('');
	filter: '';

	columns = CONTACTS_TABLE_HEADER;
	pagingSub: Subscription;
	pageSortSub: Subscription;
	paging: PaginationParams = {
		size: CONTACTS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: CONTACTS_DEFAULT_SORT_COLUMN,
		order: CONTACTS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = CONTACTS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = CONTACTS_PAGE_SIZE_OPTIONS;

	maxSizeMemorySub: Subscription;
	memorySize = 1;
	maxMemorySize = 1;

	loading$: Observable<boolean>;
	mtmaContacts: MtmaContact[];

	mtmaCommandAutomation?: Command[];

	contactsLoading: boolean;

	totalRecords = 0;

	currentSite: Site;

	langSub: Subscription;

	mtmaActions: MtmaAction[] = [];
	mtmaActionsSuccessSub: Subscription;
	confirmDeleteSub: Subscription;

	isActiveTask = false;
	dateTransform: DatePipe;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 967;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_MX7 = 1700;
	LG_WIDTH = 1500;

	MAX_MD_WIDTH_SHORT = 858;
	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.mtmaContacts$ = this.store.select(selectMtmaContacts);
		this.mtmaContactsPagination$ = this.store.select(selectMtmaContactsPagination);
		this.mtmaContactsMaxData$ = this.store.select(selectMtmaContactsMaxData);
		this.mtmaCommandAutomation$ = this.store.select(selectMtmaCommandAutomation);
		this.mtmaActions$ = this.store.select(selectMtmaActions);
		this.mtmaContactModify$ = this.store.select(selectMtmaContactModify);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
	}

	ngOnInit(): void {
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);
		this.dispatchGetContacts();
		this.store.dispatch(DeviceActions.getMtmaActions());
		this.mtmaContactsSub = this.mtmaContacts$.subscribe(mtmaContacts => {
			this.mtmaContacts = mtmaContacts;
			this.contactsLoading = false;
		});

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});

		this.dispatchGetContacts();
		this.pagingSub = this.mtmaContactsPagination$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.maxSizeMemorySub = this.mtmaContactsMaxData$.pipe(filter(p => !!p))
			.subscribe(mtmaContactsMaxData => {
				this.memorySize = mtmaContactsMaxData.Current;
				this.maxMemorySize = mtmaContactsMaxData.Max;
			});

		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetContacts();
		});

		this.mtmaCommandAutomationSub = this.mtmaCommandAutomation$.subscribe(mtmaCommandAutomation => {
			this.mtmaCommandAutomation = mtmaCommandAutomation;
			this.createTranslationAction();
		});

		this.mtmaActionsSub = this.mtmaActions$.subscribe(mtmaActions => {
			this.mtmaActions = mtmaActions;
			this.getAutomationCommand();
			this.createTranslationAction();
		});

		this.langSub = this.currentLang$.subscribe(() => {
			this.store.dispatch(DeviceActions.getMtmaActions());
		});

		this.mtmaContactModifySub = this.mtmaContactModify$.pipe(filter(mtmaContactModifySub => !!mtmaContactModifySub))
			.subscribe(() => {
				this.dispatchGetContacts();
			});

		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id !== '') {
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetContacts();
			});

		this.onResize();
	}

	ngOnDestroy(): void {
		this.mtmaActionsSub?.unsubscribe();
		this.mtmaCommandAutomationSub?.unsubscribe();
		this.langSub?.unsubscribe();
		this.mtmaContactModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.maxSizeMemorySub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
	}

	loadContacts(event: TableLazyLoadEvent) {
		this.contactsLoading = true;
		this.paging = pageSortChange(this.paging, event);

		this.dispatchGetContacts();
	}

	dispatchGetContacts(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaContacts({
				filter: this.filter,
				sort: this.paging.sort, order: this.paging.order, size: this.paging.size, page: this.paging.page
			}));
		}
	}

	isSelectTab(): boolean {
		return this.tabIndex === MtmaIndexTab.MTM_CONTACTS;
	}

	addContact(): void {
		this.store.dispatch(DeviceActions.resetContactForm());
		this.ref = this.dialogService.open(MtmaContactDialogComponent, {
			header: this.translate.instant('ADD_CONTACT'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editContact(contact: MtmaContact): void {
		this.store.dispatch(DeviceActions.resetContactForm());
		this.ref = this.dialogService.open(MtmaContactDialogComponent, {
			header: this.translate.instant('EDIT_CONTACT'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: { contact },
		});
	}

	createTranslationAction(): void {
		if (this.mtmaActions && this.mtmaCommandAutomation) {
			for (const action of this.mtmaActions) {
				if (action.AutomationId) {
					action.Traslation = this.getTranslationAction(action);
				} else {
					action.Traslation = this.translate.instant(action.Description);
				}
			}
		}
	}

	getTranslationAction(action: MtmaAction): string {
		let nameDevice = '';
		if (this.currentSite && this.currentSite.Devices) {
			for (const device of this.currentSite.Devices) {
				if (device.Id === action.AutomationId) {
					nameDevice = device.Name + ' - ';
				}
			}
		}
		for (const command of this.mtmaCommandAutomation) {
			if (action.AutomationId === command.AutomationId && action.Command === command.Id) {
				return nameDevice + command.Description;
			}
		}
		return nameDevice;
	}

	async getAutomationCommand(): Promise<void> {
		const mapAutomation = new Map<number, number>();
		for (const action of this.mtmaActions) {
			if (action.AutomationId && !mapAutomation.has(action.AutomationId)) {
				mapAutomation.set(action.AutomationId, action.AutomationId);
			}
		}
		let cont = 0;
		for (const key of mapAutomation.keys()) {
			setTimeout(() => {
				this.store.dispatch(DeviceActions.getMtmaAutomationCommands({ deviceId: key }));
			}, cont * 1000);
			cont++;
		}
	}

	getTextAction(contact: MtmaContact): string {
		let text = '';
		if (contact && contact.Actions) {
			for (const action of contact.Actions) {
				for (const actionAll of this.mtmaActions) {
					if (action === actionAll.Id) {
						if (text === '') {
							text = actionAll.Traslation;
						} else {
							text = text + ', ' + actionAll.Traslation;
						}
						break;
					}
				}
			}
		}
		return text;
	}

	delete(contact: MtmaContact): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_CONTACT'),
			message: this.translate.instant('DELETE_CONTACT_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteMtmaContact({ contactId: contact.ObjId }));
			},
			reject: () => {
			}
		});
	}

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}

	getUnitsContact(element: MtmaContact) {
		if ((!element.Units || element.Units.length === 0)) {
			this.store.dispatch(DeviceActions.getMtmaContact({ contact: element }));
		}
	}

	getTemporaryRule(contact: MtmaContact): string {
		if (contact.DtFrom !== undefined && contact.DtTo !== undefined) {
			return this.translate.instant('FROM') + ' ' + this.dateTransform.transform(contact.DtFrom, 'dd/MM/yyyy HH:mm') +
				' - ' + this.translate.instant('TO') + ' ' + this.dateTransform.transform(contact.DtTo, 'dd/MM/yyyy HH:mm');
		}
		return '';
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return (this.screenWidth <= 660);
	}

	componentDynamicColumn(): string {
		return (this.screenWidth <= 1367) ? (this.screenWidth <= this.MAX_MD_WIDTH ? (this.onExtendedMobile() ? 'col-12' : 'col-12 px-8') : 'col-6') : 'col-4';
	}

	layoutMarginResponsive(): string {
		return (this.screenWidth >= this.MIN_WIDTH_MX7) ? "mx-7" : "mx-2";
	}

	dynamicInputTextWidth(): string {
		return (this.screenWidth <= this.COLUMN_TITLE_MIN_WD) ? "w-full h-2_5rem" : "w-25rem h-2rem";
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}
}
